import React, { useState, useEffect, useRef } from 'react';
import { Clock, Calendar, ChevronDown, ChevronUp, FileText, CheckCircle, Loader, Heart, Clipboard, Activity, AlertCircle, Filter, X } from 'lucide-react';
import { Visit } from '../../types/patientmr/visit';
import { fetchVisitHistory, VisitHistoryFilters } from '../../services/patientmr/visits.service';
import { AssessmentsService, AssessmentListItem, AssessmentStatus } from '../../services/patientmr/assessments.service';
import { OasisListItem, OasisType } from '../../types/patientmr/oasis';
import { CarePlansService, CarePlanListItem, CarePlanStatus } from '../../services/patientmr/careplans.service';
import { fetchChartEntriesWithStatus, ChartEntry } from '../../services/patientmr/charting.service';
import PrimaryButton from '../common/PrimaryButton';
import { MobilePrimaryButton } from '../common/MobileButtons';
import { DisciplinePill, GenericListView, LoadingSpinner } from '../common';
import { StatusPill } from '../common/Pills';
import { useAuth } from '../../context/authcontext';
import { useFeature } from '../../hooks/useFeature';
import { VisitStatusBadge } from './visits/VisitStatusBadge';
import { CancelVisitModal } from './visits/CancelVisitModal';
import VisitTimeDialog from '../visit/VisitTimeDialog';
import { correctVisitTime } from '../../services/patientmr/visits.service';
import { usePermission } from '../../hooks/usePermission';
import { createPermissionString } from '../../types/permissions';
import { isApiError } from '../../dtos/common/api.dto';
import { CancelVisitRequest } from '../../dtos/patientmr/visit.dto';
import UnsyncedWorkHint from '../offline/UnsyncedWorkHint';
import { VisitNoteMenu } from './charting/components/VisitNoteActions';

interface VisitHistoryProps {
  expanded: boolean;
  toggleExpanded: () => void;
  patientId?: string;
  canCancelVisit?: boolean;
}

interface VisitData {
  assessments: AssessmentListItem[];
  oasisAssessments: OasisListItem[];
  carePlans: CarePlanListItem[];
  chartEntries: ChartEntry[];
  chartingStatus?: string; // Add charting status from charting API
}

const VisitHistory: React.FC<VisitHistoryProps> = ({
  expanded,
  toggleExpanded,
  patientId,
  canCancelVisit = false
}) => {
  const { user } = useAuth(); // Get current user for discipline mapping
  const oasisEnabled = useFeature('oasis');
  const [visits, setVisits] = useState<Visit[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [expandedVisitId, setExpandedVisitId] = useState<string | null>(null);
  // VIS-08 (Phase 9B): admin / coordinator time correction with a reason
  const canCorrectTimes = usePermission(createPermissionString('patientmr:phi:visit:correctall'));
  // Phase 12 (§4.4): "Finalize All Documentation" submits drafts, so it needs visit or charting update
  const canFinalizeDocumentation = usePermission([
    createPermissionString('patientmr:phi:visit:update'),
    createPermissionString('patientmr:phi:visit:updateall'),
    createPermissionString('patientmr:phi:charting:update'),
    createPermissionString('patientmr:phi:charting:updateall'),
  ]);
  const [correcting, setCorrecting] = useState<{ id: string; startTime?: string; endTime?: string } | null>(null);
  const [correctBusy, setCorrectBusy] = useState(false);
  const [correctError, setCorrectError] = useState<string | null>(null);
  const [visitData, setVisitData] = useState<Record<string, VisitData>>({});
  const [loadingVisitData, setLoadingVisitData] = useState<Record<string, boolean>>({});
  const [finalizingVisit, setFinalizingVisit] = useState<string | null>(null);
  const [paginationToken, setPaginationToken] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [allLoadedVisits, setAllLoadedVisits] = useState<Visit[]>([]); // All loaded visits
  const [visitPage, setVisitPage] = useState<number>(1);
  const hasLoadedVisitsRef = useRef<string | null>(null); // Track which patient's visits have been loaded
  
  // Cancel modal state
  const [cancelModal, setCancelModal] = useState<{ open: boolean; visitId: string; patientId: string }>({ open: false, visitId: '', patientId: '' });

  // Date filter state
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [hasActiveFilters, setHasActiveFilters] = useState<boolean>(false);
  
  const VISITS_PER_PAGE = 5;

  // Build filters object from current state
  const buildFilters = (): VisitHistoryFilters | undefined => {
    const filters: VisitHistoryFilters = {};
    let hasFilters = false;
    
    if (startDate) {
      filters.startDate = startDate;
      hasFilters = true;
    }
    if (endDate) {
      filters.endDate = endDate;
      hasFilters = true;
    }
    
    return hasFilters ? filters : undefined;
  };
  
  // Apply filters and reload visits
  const applyFilters = async () => {
    if (!patientId) return;
    
    try {
      setLoading(true);
      setError('');
      setShowFilters(false);
      
      const filters = buildFilters();
      setHasActiveFilters(!!filters);
      
      // Fetch first page with filters
      const response = await fetchVisitHistory(patientId, VISITS_PER_PAGE, undefined, filters);
      
      // Sort visits
      const sortedVisits = (response.visits || []).sort((a, b) => {
        if (a.endTime && b.endTime) {
          return new Date(b.endTime).getTime() - new Date(a.endTime).getTime();
        }
        if (a.endTime && !b.endTime) return 1;
        if (!a.endTime && b.endTime) return -1;
        return new Date(b.startTime).getTime() - new Date(a.startTime).getTime();
      });
      
      setAllLoadedVisits(sortedVisits);
      setVisits(sortedVisits);
      setVisitPage(1);
      setPaginationToken(response.paginationToken);
      setHasMore(response.hasMore);
    } catch (err) {
      console.error('Error applying filters:', err);
      setError('Failed to apply filters');
    } finally {
      setLoading(false);
    }
  };
  
  // Clear filters and reload all visits
  const clearFilters = async () => {
    setStartDate('');
    setEndDate('');
    setHasActiveFilters(false);
    
    if (!patientId) return;
    
    try {
      setLoading(true);
      setError('');
      
      // Fetch first page without filters
      const response = await fetchVisitHistory(patientId, VISITS_PER_PAGE);
      
      // Sort visits
      const sortedVisits = (response.visits || []).sort((a, b) => {
        if (a.endTime && b.endTime) {
          return new Date(b.endTime).getTime() - new Date(a.endTime).getTime();
        }
        if (a.endTime && !b.endTime) return 1;
        if (!a.endTime && b.endTime) return -1;
        return new Date(b.startTime).getTime() - new Date(a.startTime).getTime();
      });
      
      setAllLoadedVisits(sortedVisits);
      setVisits(sortedVisits);
      setVisitPage(1);
      setPaginationToken(response.paginationToken);
      setHasMore(response.hasMore);
    } catch (err) {
      console.error('Error clearing filters:', err);
      setError('Failed to reload visits');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get provider discipline (based on current user or default)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getProviderDiscipline = () => {
    if (!user) return "PT"; // Default to PT if no user
    
    const providerType = user.providerType;
    
    // If no providerType is set, default to PT
    if (!providerType) return "PT";
    
    // Map provider types to standard discipline codes
    if (providerType.includes('PT') || providerType.includes('Physical')) {
      return "PT";
    } else if (providerType.includes('OT') || providerType.includes('Occupational')) {
      return "OT";
    } else if (providerType.includes('SLP') || providerType.includes('Speech') || providerType.includes('ST')) {
      return "ST";
    } else if (
      providerType.includes('RN') ||
      providerType.includes('Nurse')
    ) {
      return "RN";
    } else {
      return providerType;
    }
  };

  // Helper function to map AssessmentListItem to OasisListItem (same as OASIS component)
  const mapAssessmentToOasisItem = (assessment: AssessmentListItem): OasisListItem => {
    // Determine OASIS type based on available information
    // Default to OASIS_SOC if not available
    let oasisType = OasisType.OASIS_SOC;
    
    // No status mapping needed - use AssessmentStatus directly!
    
    return {
      assessmentId: assessment.assessmentId,
      patientId: patientId || '',
      type: oasisType,
      assessmentDate: assessment.date,
      status: assessment.status, // Use AssessmentStatus directly, no mapping
      hippsCode: '', // We'll get this from detailed assessment data when needed
      clinicianName: assessment.providerName,
      createdAt: assessment.date,
      updatedAt: assessment.date
    };
  };

  // Load visit history when component mounts or patientId changes
  useEffect(() => {
    const loadVisitHistory = async () => {
      if (!patientId) return;
      
      // Skip if already loaded for this patient or if currently loading
      if (hasLoadedVisitsRef.current === patientId) {
        return;
      }
      
      try {
        setLoading(true);
        setError('');
        hasLoadedVisitsRef.current = patientId; // Mark as loading immediately to prevent duplicate calls
        
        // Fetch first page (5 visits)
        const response = await fetchVisitHistory(patientId, VISITS_PER_PAGE);
        
        // Sort by end date (descending - most recent first), then by start date if no end date
        const sortedVisits = (response.visits || []).sort((a, b) => {
          // If both have end dates, sort by end date descending
          if (a.endTime && b.endTime) {
            return new Date(b.endTime).getTime() - new Date(a.endTime).getTime();
          }
          // If only one has end date, the one without end date (in progress) comes first
          if (a.endTime && !b.endTime) return 1;
          if (!a.endTime && b.endTime) return -1;
          // If neither has end date, sort by start date descending
          return new Date(b.startTime).getTime() - new Date(a.startTime).getTime();
        });
        
        setAllLoadedVisits(sortedVisits);
        setVisits(sortedVisits);
        setVisitPage(1);
        setPaginationToken(response.paginationToken);
        setHasMore(response.hasMore);
      } catch (err) {
        console.error('Error fetching visit history:', err);
        setError('Failed to load visit history');
        // Keep ref set to patientId so a failed load doesn't trigger an infinite retry loop.
        // User can collapse/re-expand to retry manually.
      } finally {
        setLoading(false);
      }
    };

    if (expanded && patientId) {
      loadVisitHistory();
    }

    // Reset the loaded flag when component is collapsed so it reloads fresh data when re-expanded
    if (!expanded) {
      hasLoadedVisitsRef.current = null;
      setPaginationToken(undefined);
      setAllLoadedVisits([]);
      setVisits([]);
      setVisitPage(1);
      setHasMore(false);
    }
  }, [expanded, patientId]); // removed `loading` — it caused infinite retry on API error

  // Load a requested page, fetching the next cursor only when needed.
  const handleVisitPageChange = async (page: number) => {
    if (page < 1 || loadingMore) return;

    const loadedPages = Math.ceil(allLoadedVisits.length / VISITS_PER_PAGE);
    if (page <= loadedPages) {
      setVisitPage(page);
      setVisits(allLoadedVisits.slice((page - 1) * VISITS_PER_PAGE, page * VISITS_PER_PAGE));
      setExpandedVisitId(null);
      return;
    }

    if (!patientId || !paginationToken || !hasMore) return;

    try {
      setLoadingMore(true);

      let updatedVisits = allLoadedVisits;
      let nextToken = paginationToken;
      let nextHasMore: boolean = hasMore;
      let loadedPageCount = loadedPages;

      while (page > loadedPageCount && nextToken && nextHasMore) {
        const response = await fetchVisitHistory(patientId, VISITS_PER_PAGE, nextToken, buildFilters());
        const sortedNewVisits = (response.visits || []).sort((a, b) => {
          if (a.endTime && b.endTime) return new Date(b.endTime).getTime() - new Date(a.endTime).getTime();
          if (a.endTime && !b.endTime) return 1;
          if (!a.endTime && b.endTime) return -1;
          return new Date(b.startTime).getTime() - new Date(a.startTime).getTime();
        });
        updatedVisits = [...updatedVisits, ...sortedNewVisits];
        nextToken = response.paginationToken;
        nextHasMore = response.hasMore;
        loadedPageCount = Math.ceil(updatedVisits.length / VISITS_PER_PAGE);
      }

      setAllLoadedVisits(updatedVisits);
      setVisits(updatedVisits.slice((page - 1) * VISITS_PER_PAGE, page * VISITS_PER_PAGE));
      setVisitPage(page);
      setPaginationToken(nextToken);
      setHasMore(nextHasMore);
      setExpandedVisitId(null);
    } catch (err) {
      console.error('Error loading visit page:', err);
      setError('Failed to load visit page');
    } finally {
      setLoadingMore(false);
    }
  };

  // Load all data for a specific visit
  const loadDataForVisit = async (visitId: string, forceRefresh: boolean = false) => {
    if (!patientId || (!forceRefresh && visitData[visitId])) {
      return; // Already loaded or no patient (skip unless force refresh)
    }

    try {
      setLoadingVisitData(prev => ({ ...prev, [visitId]: true }));
      
      // Load all data types in parallel
      const [assessments, oasisAssessments, carePlans, chartData] = await Promise.allSettled([
        AssessmentsService.getAssessmentsByVisit(patientId, visitId),
        // OASIS is hidden unless the oasis flag is on (the API returns 404 when it's off)
        oasisEnabled
          ? AssessmentsService.getAssessments(patientId, { visitId, category: 'oasis' }) // Get OASIS assessments filtered by visitId
          : Promise.resolve({ assessments: [] as AssessmentListItem[], hasMore: false }),
        CarePlansService.getCarePlans(patientId, visitId), // Get care plans for this specific visit
        fetchChartEntriesWithStatus(patientId, visitId) // Use the new function that returns charting status
      ]);

  // OASIS and care plans are now both filtered by the backend using visitId
  const getFilteredData = () => {
    return {
      oasisAssessments: (oasisAssessments.status === 'fulfilled' ? oasisAssessments.value.assessments : [])
        .map(assessment => mapAssessmentToOasisItem(assessment)), // Convert AssessmentListItem to OasisListItem
      carePlans: (carePlans.status === 'fulfilled' ? carePlans.value.carePlans : []) // Already filtered by backend
    };
  };

      const filteredData = getFilteredData();

      // Extract chart entries and charting status
      const chartResult = chartData.status === 'fulfilled' ? chartData.value : { chartEntries: [], chartingStatus: 'NOT_STARTED' };

      console.log(`[VisitHistory] Chart result for visit ${visitId}:`, chartResult);
      console.log(`[VisitHistory] Charting status:`, chartResult.chartingStatus);

      const visitDataResult: VisitData = {
        assessments: assessments.status === 'fulfilled' ? assessments.value.assessments : [],
        oasisAssessments: filteredData.oasisAssessments,
        carePlans: filteredData.carePlans,
        chartEntries: chartResult.chartEntries,
        chartingStatus: chartResult.chartingStatus // Store the charting status from API
      };

      console.log(`[VisitHistory] Visit data result:`, visitDataResult);

      setVisitData(prev => ({
        ...prev,
        [visitId]: visitDataResult
      }));
    } catch (err) {
      console.error(`Error fetching data for visit ${visitId}:`, err);
      // Set empty data to indicate we tried to load but failed
      setVisitData(prev => ({
        ...prev,
        [visitId]: {
          assessments: [],
          oasisAssessments: [],
          carePlans: [],
          chartEntries: [],
          chartingStatus: 'NOT_STARTED'
        }
      }));
    } finally {
      setLoadingVisitData(prev => ({ ...prev, [visitId]: false }));
    }
  };

  // Handle visit selection/expansion
  const handleVisitToggle = (visitId: string) => {
    if (expandedVisitId === visitId) {
      // Collapse if already expanded
      setExpandedVisitId(null);
    } else {
      // Expand new visit and collapse others
      setExpandedVisitId(visitId);
      // Always refresh data when expanding to get latest status
      loadDataForVisit(visitId, true);
    }
  };

  // Handle submit all documentation
  const handleFinalizeAllDocumentation = async (visitId: string) => {
    if (!patientId) return;

    const data = visitData[visitId];
    if (!data) return;

    const draftAssessments = data.assessments.filter(assessment => assessment.status === AssessmentStatus.DRAFT);
    const draftOasis = data.oasisAssessments.filter(oasis => oasis.status === AssessmentStatus.DRAFT);
    const draftCarePlans = data.carePlans.filter(plan => plan.status === CarePlanStatus.DRAFT);
    const chartingStatus = getChartingStatusFromVisitData(data);
    const hasIncompleteCharting = !chartingStatus.isComplete;

    const totalDrafts = draftAssessments.length + draftOasis.length + draftCarePlans.length;

    if (totalDrafts === 0 && !hasIncompleteCharting) {
      alert('All documentation is already complete for this visit.');
      return;
    }

    let confirmMessage = '';
    if (totalDrafts > 0 && hasIncompleteCharting) {
      confirmMessage = `This will submit ${totalDrafts} draft document(s) for this visit (${draftAssessments.length} assessments, ${draftOasis.length} OASIS, ${draftCarePlans.length} care plans).\n\nNote: Charting status is "${chartingStatus.status}" - please ensure all required chart entries are completed separately.\n\nContinue with submission?`;
    } else if (totalDrafts > 0) {
      confirmMessage = `This will submit ${totalDrafts} draft document(s) for this visit (${draftAssessments.length} assessments, ${draftOasis.length} OASIS, ${draftCarePlans.length} care plans). Are you sure you want to continue?`;
    } else {
      alert(`Charting status is "${chartingStatus.status}". Please complete all required chart entries in the Charting section.`);
      return;
    }

    const confirmFinalize = window.confirm(confirmMessage);

    if (!confirmFinalize) return;

    try {
      setFinalizingVisit(visitId);

      // Submit all draft items in parallel (Phase 19 F7: the former POST …/complete calls had no
      // route; submit runs the same required-field checks as the forms and sends them to QA)
      const promises = [
        ...draftAssessments.map(assessment =>
          AssessmentsService.submitAssessment(patientId, assessment.assessmentId)
        ),
        ...draftOasis.map(oasis =>
          AssessmentsService.submitAssessment(patientId, oasis.assessmentId)
        ),
        ...draftCarePlans.map(plan =>
          CarePlansService.submitCarePlan(patientId, plan.carePlanId)
        )
      ];

      await Promise.all(promises);

      // Update local state to reflect completed items
      setVisitData(prev => ({
        ...prev,
        [visitId]: {
          ...prev[visitId],
          assessments: prev[visitId].assessments.map(assessment =>
            draftAssessments.some(draft => draft.assessmentId === assessment.assessmentId)
              ? { ...assessment, status: AssessmentStatus.SUBMITTED }
              : assessment
          ),
          oasisAssessments: prev[visitId].oasisAssessments.map(oasis =>
            draftOasis.some(draft => draft.assessmentId === oasis.assessmentId)
              ? { ...oasis, status: AssessmentStatus.SUBMITTED }
              : oasis
          ),
          carePlans: prev[visitId].carePlans.map(plan =>
            draftCarePlans.some(draft => draft.carePlanId === plan.carePlanId)
              ? { ...plan, status: CarePlanStatus.SUBMITTED }
              : plan
          )
        }
      }));

      alert(`Successfully submitted ${totalDrafts} document(s).${hasIncompleteCharting ? ` Note: Please complete charting separately (status: ${chartingStatus.status}).` : ' All documentation is now complete!'}`);
    } catch (err) {
      console.error('Error submitting documentation:', err);
      alert('Failed to submit some documentation. Please try again.');
    } finally {
      setFinalizingVisit(null);
    }
  };

  // Format date for display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Format time for display
  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Calculate visit duration
  const calculateDuration = (startTime: string, endTime?: string) => {
    if (!endTime) return 'In Progress';
    
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end.getTime() - start.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 60) {
      return `${diffMins}m`;
    } else {
      const hours = Math.floor(diffMins / 60);
      const mins = diffMins % 60;
      return `${hours}h ${mins > 0 ? `${mins}m` : ''}`;
    }
  };



  // Get assessment status badge style
  const getAssessmentStatusBadge = (status: AssessmentStatus) => {
    switch (status) {
      case AssessmentStatus.COMPLETE:
        return 'bg-green-100 text-green-800';
      case AssessmentStatus.CURRENT:
        return 'bg-blue-100 text-blue-800';
      case AssessmentStatus.DRAFT:
        return 'bg-yellow-100 text-yellow-800';
      case AssessmentStatus.EXPIRED:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get care plan status badge style
  const getCarePlanStatusBadge = (status: CarePlanStatus) => {
    switch (status) {
      case CarePlanStatus.COMPLETE:
        return 'bg-green-100 text-green-800';
      case CarePlanStatus.CURRENT:
        return 'bg-blue-100 text-blue-800';
      case CarePlanStatus.DRAFT:
        return 'bg-yellow-100 text-yellow-800';
      case CarePlanStatus.EXPIRED:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Format status for display
  const formatStatus = (status: string) => {
    return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Get charting status from visit data's chartingStatus field
  const getChartingStatusFromVisitData = (visitData: VisitData) => {
    const chartingStatus = visitData.chartingStatus;
    
    console.log(`[VisitHistory] getChartingStatusFromVisitData called with:`, visitData);
    console.log(`[VisitHistory] chartingStatus value:`, chartingStatus);
    
    switch (chartingStatus) {
      case 'NOT_STARTED':
        return { status: 'Not Started', color: 'bg-gray-100 text-gray-800', icon: AlertCircle, isComplete: false };
      case 'IN_PROGRESS':
        return { status: 'In Progress', color: 'bg-blue-100 text-blue-800', icon: Activity, isComplete: false };
      case 'COMPLETE':
        return { status: 'Complete', color: 'bg-green-100 text-green-800', icon: CheckCircle, isComplete: true };
      case 'FINALIZED':
        return { status: 'Finalized', color: 'bg-green-100 text-green-800', icon: CheckCircle, isComplete: true };
      case 'SUBMITTED':
        return { status: 'Submitted', color: 'bg-blue-100 text-blue-800', icon: CheckCircle, isComplete: true };
      default:
        // Fallback to 'Not Started' if chartingStatus is undefined or unknown
        console.log(`[VisitHistory] Unknown charting status, using fallback:`, chartingStatus);
        return { status: 'Not Started', color: 'bg-gray-100 text-gray-800', icon: AlertCircle, isComplete: false };
    }
  };

  // Calculate overall visit status based on all associated documents
  const getVisitDocumentationStatus = (data: VisitData): { status: string; color: string; description: string } => {
    // Collect all document statuses
    const allStatuses: string[] = [];
    
    // Add charting status
    if (data.chartingStatus) {
      allStatuses.push(data.chartingStatus);
    }
    
    // Add assessment statuses
    data.assessments.forEach(assessment => {
      allStatuses.push(assessment.status);
    });
    
    // Add OASIS statuses
    data.oasisAssessments.forEach(oasis => {
      allStatuses.push(oasis.status);
    });
    
    // Add care plan statuses
    data.carePlans.forEach(plan => {
      allStatuses.push(plan.status);
    });
    
    // If no documents at all, return NOT_STARTED
    if (allStatuses.length === 0) {
      return {
        status: 'NOT_STARTED',
        color: 'bg-gray-100 text-gray-800',
        description: 'No documentation started'
      };
    }
    
    // Check for DRAFT status (DOCPENDING)
    // Note: 'IN_PROGRESS' is a legacy alias normalized to 'DRAFT' by the backend on read
    const hasDraft = allStatuses.some(status =>
      status === 'DRAFT' ||
      status === 'NOT_STARTED'
    );
    
    if (hasDraft) {
      return {
        status: 'DOCPENDING',
        color: 'bg-yellow-100 text-yellow-800',
        description: 'Documentation pending'
      };
    }
    
    // Check for QA status
    // Note: 'QA_APPROVED' is a legacy alias normalized to 'COMPLETE' by the backend on read
    const hasQA = allStatuses.some(status => status === 'QA');
    const hasNonQA = allStatuses.some(status =>
      status !== 'QA' &&
      status !== 'COMPLETE' &&
      status !== 'FINALIZED'
    );
    
    if (hasQA && !hasNonQA) {
      return {
        status: 'QA',
        color: 'bg-blue-100 text-blue-800',
        description: 'Under quality assurance review'
      };
    }
    
    // Check if all are COMPLETE/FINALIZED
    // Note: 'QA_APPROVED' is a legacy alias normalized to 'COMPLETE' by the backend on read
    const allComplete = allStatuses.every(status =>
      status === 'COMPLETE' ||
      status === 'FINALIZED'
    );
    
    if (allComplete) {
      return {
        status: 'COMPLETE',
        color: 'bg-green-100 text-green-800',
        description: 'All documentation complete'
      };
    }
    
    // Check if all are SUBMITTED (and none are DRAFT/COMPLETE/QA)
    const allSubmitted = allStatuses.every(status => 
      status === 'SUBMITTED' || 
      status === 'LOCKED'
    );
    
    if (allSubmitted) {
      return {
        status: 'SUBMITTED',
        color: 'bg-blue-100 text-blue-800',
        description: 'All documentation submitted'
      };
    }
    
    // Mixed states - some submitted, some complete, some QA
    return {
      status: 'IN_REVIEW',
      color: 'bg-blue-100 text-blue-800',
      description: 'Documentation in review'
    };
  };

  // Check if finalization is needed for a visit
  const shouldShowFinalizeButton = (visitId: string, data: VisitData) => {
    const chartingStatus = getChartingStatusFromVisitData(data);
    const hasIncompleteCharting = !chartingStatus.isComplete;
    
    const hasIncompleteAssessments = data.assessments.some(assessment => 
      assessment.status === AssessmentStatus.DRAFT
    );
    
    const hasIncompleteOasis = data.oasisAssessments.some(oasis => 
      oasis.status === AssessmentStatus.DRAFT
    );
    
    const hasIncompleteCarePlans = data.carePlans.some(plan => 
      plan.status === CarePlanStatus.DRAFT
    );
    
    return hasIncompleteCharting || hasIncompleteAssessments || hasIncompleteOasis || hasIncompleteCarePlans;
  };

  const filterControls = (
    <div className="w-full">
      <div className="flex items-center justify-between gap-2">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center space-x-2 px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
        >
          <Filter className="h-4 w-4" />
          <span>Filters</span>
          {hasActiveFilters && (
            <span className="ml-2 px-2 py-0.5 text-xs bg-slate-600 text-white rounded-full">Active</span>
          )}
        </button>
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="flex items-center space-x-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <X className="h-4 w-4" />
            <span>Clear Filters</span>
          </button>
        )}
      </div>
      {showFilters && (
        <div className="mt-3 bg-gray-50 border border-gray-200 rounded-md p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <button
              onClick={() => setShowFilters(false)}
              className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <PrimaryButton onClick={applyFilters}>Apply Filters</PrimaryButton>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="mb-0 md:mb-2">
      {/* Phase 16C C10: visits that ended offline on a phone arrive after it syncs */}
      <UnsyncedWorkHint patientId={patientId} className="mx-4 my-2" />
      {/* Header */}
      <div
        className="md:hidden py-4 px-4 flex items-center justify-between cursor-pointer bg-slate-50 hover:bg-slate-100 active:bg-slate-200 transition-colors border-b border-slate-200"
        onClick={toggleExpanded}
      >
        <h2 className="text-lg font-semibold flex items-center text-slate-800">
          <Clock size={20} className="mr-3 text-slate-600" />
          Visit History
        </h2>
        <ChevronDown
          size={20}
          className={`transition-transform text-slate-600 ${expanded ? 'rotate-180' : ''}`}
        />
      </div>

      {/* Content */}
      {expanded && (
        <div>
          {loading ? (
            <LoadingSpinner />
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600">{error}</p>
              <button
                onClick={() => {
                  if (patientId) {
                    const loadVisitHistory = async () => {
                      try {
                        setLoading(true);
                        setError('');
                        hasLoadedVisitsRef.current = null; // Reset the ref so it can reload
                        
                        // Fetch first page
                        const response = await fetchVisitHistory(patientId, VISITS_PER_PAGE);
                        
                        // Sort visits
                        const sortedVisits = (response.visits || []).sort((a, b) => {
                          if (a.endTime && b.endTime) {
                            return new Date(b.endTime).getTime() - new Date(a.endTime).getTime();
                          }
                          if (a.endTime && !b.endTime) return 1;
                          if (!a.endTime && b.endTime) return -1;
                          return new Date(b.startTime).getTime() - new Date(a.startTime).getTime();
                        });
                        
                        setAllLoadedVisits(sortedVisits);
                        setVisits(sortedVisits);
                        setPaginationToken(response.paginationToken);
                        setHasMore(response.hasMore);
                        
                        hasLoadedVisitsRef.current = patientId; // Mark as loaded
                      } catch (err) {
                        console.error('Error fetching visit history:', err);
                        setError('Failed to load visit history');
                      } finally {
                        setLoading(false);
                      }
                    };
                    loadVisitHistory();
                  }
                }}
                className="mt-2 px-4 py-2 bg-slate-600 text-white rounded-md hover:bg-slate-700"
              >
                Retry
              </button>
            </div>
          ) : visits.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-500">No visit history available</p>
            </div>
          ) : (
            <GenericListView
              title=""
              expanded
              onToggleExpand={() => undefined}
              hideHeader
              items={visits}
              keyExtractor={(visit) => visit.id}
              columns={[]}
              mobileCard={{ primaryText: () => '' }}
              showRefresh={false}
              showNew={false}
              showSearch={false}
              showFilters={false}
              actionBarChildren={filterControls}
              actionBarChildrenAlign="end"
              className="space-y-1.5"
              contentClassName="pt-2 md:pt-2 lg:pt-2"
              hasLoadedOnce
              emptyStateIcon={<Clock size={48} className="text-gray-300" />}
              emptyStateTitle="No visit history available"
              emptyStateMessage="No visits were found for this patient."
              pagination={{
                currentPage: visitPage,
                totalPages: Math.max(1, Math.ceil(allLoadedVisits.length / VISITS_PER_PAGE) + (hasMore ? 1 : 0)),
                pageSize: VISITS_PER_PAGE,
                totalCount: hasMore ? undefined : allLoadedVisits.length,
                onPageChange: handleVisitPageChange,
              }}
              renderItem={(visit, index) => (
                <div
                  key={visit.id || index}
                  className={`border border-gray-200 rounded-lg overflow-hidden${visit.status === 'CANCELLED' ? ' opacity-75' : ''}`}
                >
                  {/* Visit Header - Clickable */}
                  <div
                    className="p-4 hover:bg-gray-50 cursor-pointer min-h-[36px]"
                    onClick={() => handleVisitToggle(visit.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <Calendar className="h-4 w-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-900">
                            {formatDate(visit.startTime)}
                          </span>
                          <span className="text-sm text-gray-500">
                            {formatTime(visit.startTime)}
                            {visit.endTime && ` - ${formatTime(visit.endTime)}`}
                          </span>
                        </div>

                        <div className="flex items-center flex-wrap gap-2 text-sm text-gray-600">
                          {visit.discipline && (
                            <DisciplinePill discipline={visit.discipline} />
                          )}
                          <VisitStatusBadge status={visit.status} size="sm" />
                          {calculateDuration(visit.startTime, visit.endTime) === 'In Progress' ? null : (
                            <span>Duration: {calculateDuration(visit.startTime, visit.endTime)}</span>
                          )}
                          {canCancelVisit && (visit.status === 'SCHEDULED' || visit.status === 'INPROGRESS') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setCancelModal({ open: true, visitId: visit.id || '', patientId: patientId || '' });
                              }}
                              className="text-xs text-red-600 hover:text-red-800 font-medium"
                            >
                              Cancel Visit
                            </button>
                          )}
                          {canCorrectTimes && (visit.status === 'DOCPENDING' || visit.status === 'READYFORBILLING' || visit.status === 'COMPLETE') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setCorrectError(null);
                                setCorrecting({ id: visit.id || '', startTime: visit.startTime, endTime: visit.endTime });
                              }}
                              className="min-h-[32px] text-xs font-medium text-slate-700 hover:text-slate-900"
                            >
                              Correct times
                            </button>
                          )}
                          {/* Phase 18 M9: signed visit note (internal; external copy with the export permission) */}
                          {patientId && visit.id && visit.status !== 'SCHEDULED' && visit.status !== 'CANCELLED' && visit.status !== 'INPROGRESS' && (
                            <VisitNoteMenu patientId={patientId} visitId={visit.id} />
                          )}
                        </div>

                        {visit.notes && (
                          <p className="mt-2 text-sm text-gray-600 truncate">
                            {visit.notes}
                          </p>
                        )}
                      </div>

                      <div className="ml-4">
                        {expandedVisitId === visit.id ? (
                          <ChevronUp className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Visit Details Section */}
                  {expandedVisitId === visit.id && (
                    <div className="border-t border-gray-200 bg-gray-50">
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <h4 className="text-sm font-medium text-gray-900">
                              Visit Documentation
                            </h4>
                            {visitData[visit.id] && (() => {
                              const docStatus = getVisitDocumentationStatus(visitData[visit.id]);
                              return (
                                <span 
                                  className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${docStatus.color}`}
                                  title={docStatus.description}
                                >
                                  {docStatus.status}
                                </span>
                              );
                            })()}
                          </div>
                          {canFinalizeDocumentation && visitData[visit.id] && shouldShowFinalizeButton(visit.id, visitData[visit.id]) && (
                            <div className="hidden sm:block">
                              <PrimaryButton
                                onClick={() => handleFinalizeAllDocumentation(visit.id)}
                                disabled={finalizingVisit === visit.id}
                                className="text-sm py-1 px-3 inline-flex items-center"
                              >
                                {finalizingVisit === visit.id ? (
                                  <>
                                    <Loader className="h-3 w-3 mr-1 animate-spin" />
                                    Submitting...
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Submit all
                                  </>
                                )}
                              </PrimaryButton>
                            </div>
                          )}
                          {canFinalizeDocumentation && visitData[visit.id] && shouldShowFinalizeButton(visit.id, visitData[visit.id]) && (
                            <div className="block sm:hidden">
                              <MobilePrimaryButton
                                onClick={() => handleFinalizeAllDocumentation(visit.id)}
                                disabled={finalizingVisit === visit.id}
                                className="text-xs py-1 px-3 inline-flex items-center"
                              >
                                {finalizingVisit === visit.id ? (
                                  <>
                                    <Loader className="h-3 w-3 mr-1 animate-spin" />
                                    Submitting...
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Submit all
                                  </>
                                )}
                              </MobilePrimaryButton>
                            </div>
                          )}
                        </div>

                        {loadingVisitData[visit.id] ? (
                          <div className="flex justify-center py-8">
                            <Loader size={24} className="animate-spin text-slate-600" />
                          </div>
                        ) : visitData[visit.id] ? (
                          <div className="space-y-4">
                            {/* Charting Status - First Item */}
                            <div className="bg-white border border-gray-200 rounded-md p-3">
                              <div className="flex items-center justify-between mb-2">
                                <h5 className="text-sm font-medium text-gray-900 flex items-center">
                                  <Activity className="h-4 w-4 mr-2 text-blue-600" />
                                  Charting Status
                                </h5>
                              </div>
                              {(() => {
                                const chartingStatus = getChartingStatusFromVisitData(visitData[visit.id]);
                                const StatusIcon = chartingStatus.icon;
                                return (
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                      <StatusIcon className="h-4 w-4 text-gray-500" />
                                      <span className="text-sm text-gray-700">
                                        {visitData[visit.id].chartEntries.length} chart entries
                                      </span>
                                    </div>
                                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${chartingStatus.color}`}>
                                      {chartingStatus.status}
                                    </span>
                                  </div>
                                );
                              })()}
                            </div>

                            {/* Assessments Section */}
                            {visitData[visit.id].assessments.length > 0 && (
                              <div className="bg-white border border-gray-200 rounded-md p-3">
                                <h5 className="text-sm font-medium text-gray-900 flex items-center mb-2">
                                  <FileText className="h-4 w-4 mr-2 text-blue-600" />
                                  Assessments ({visitData[visit.id].assessments.length})
                                </h5>
                                <div className="space-y-2">
                                  {visitData[visit.id].assessments.map((assessment, assessmentIndex) => (
                                    <div
                                      key={assessment.assessmentId || assessmentIndex}
                                      className="flex items-start justify-between p-2 bg-gray-50 rounded"
                                    >
                                      <div className="flex-1 text-left">
                                        <div className="text-xs font-medium text-gray-900 mb-1">
                                          {assessment.discipline} Assessment
                                        </div>
                                        <div className="text-xs text-gray-500">
                                          Provider: {assessment.providerName} • {formatDate(assessment.date)}
                                        </div>
                                      </div>
                                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getAssessmentStatusBadge(assessment.status)} ml-2 flex-shrink-0`}>
                                        {formatStatus(assessment.status)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* OASIS Assessments Section */}
                            {visitData[visit.id].oasisAssessments.length > 0 && (
                              <div className="bg-white border border-gray-200 rounded-md p-3">
                                <h5 className="text-sm font-medium text-gray-900 flex items-center mb-2">
                                  <Heart className="h-4 w-4 mr-2 text-red-600" />
                                  OASIS Assessments ({visitData[visit.id].oasisAssessments.length})
                                </h5>
                                <div className="space-y-2">
                                  {visitData[visit.id].oasisAssessments.map((oasis, oasisIndex) => (
                                    <div
                                      key={oasis.assessmentId || oasisIndex}
                                      className="flex items-start justify-between p-2 bg-gray-50 rounded"
                                    >
                                      <div className="flex-1 text-left">
                                        <div className="flex items-center space-x-2 mb-1">
                                          <span className="text-xs font-medium text-gray-900">
                                            {formatStatus(oasis.type)}
                                          </span>
                                          {oasis.hippsCode && (
                                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                                              {oasis.hippsCode}
                                            </span>
                                          )}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                          Clinician: {oasis.clinicianName} • {formatDate(oasis.assessmentDate)}
                                        </div>
                                      </div>
                                      <StatusPill status={oasis.status} className="ml-2 flex-shrink-0" />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Care Plans Section */}
                            {visitData[visit.id].carePlans.length > 0 && (
                              <div className="bg-white border border-gray-200 rounded-md p-3">
                                <h5 className="text-sm font-medium text-gray-900 flex items-center mb-2">
                                  <Clipboard className="h-4 w-4 mr-2 text-green-600" />
                                  Care Plans ({visitData[visit.id].carePlans.length})
                                </h5>
                                <div className="space-y-2">
                                  {visitData[visit.id].carePlans.map((plan, planIndex) => (
                                    <div
                                      key={plan.carePlanId || planIndex}
                                      className="flex items-start justify-between p-2 bg-gray-50 rounded"
                                    >
                                      <div className="flex-1 text-left">
                                        <div className="text-xs font-medium text-gray-900 mb-1">
                                          {plan.discipline} Care Plan
                                        </div>
                                        <div className="text-xs text-gray-500">
                                          Provider: {plan.providerName} • {formatDate(plan.date)}
                                        </div>
                                      </div>
                                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getCarePlanStatusBadge(plan.status)} ml-2 flex-shrink-0`}>
                                        {formatStatus(plan.status)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Empty State */}
                            {!visitData[visit.id].assessments.length && 
                             !visitData[visit.id].oasisAssessments.length && 
                             !visitData[visit.id].carePlans.length && 
                             !visitData[visit.id].chartEntries.length && (
                              <div className="text-center py-6">
                                <FileText className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                                <p className="text-sm text-gray-500">No documentation found for this visit</p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-6">
                            <AlertCircle className="mx-auto h-8 w-8 text-red-400 mb-2" />
                            <p className="text-sm text-red-500">Failed to load visit documentation</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            />
          )}
        </div>
      )}
      {canCorrectTimes && (
        <VisitTimeDialog
          open={Boolean(correcting)}
          mode="correct"
          summary="The original times are kept in the visit history. An EVV-verified visit keeps its EVV minutes."
          start={correcting?.startTime || null}
          end={correcting?.endTime || null}
          busy={correctBusy}
          error={correctError}
          onCancel={() => setCorrecting(null)}
          onConfirm={async (times, reason) => {
            if (!correcting || !patientId) return;
            setCorrectBusy(true);
            setCorrectError(null);
            try {
              const updated = await correctVisitTime(patientId, correcting.id, times, reason);
              const patch = { startTime: times.startTime || correcting.startTime, endTime: times.endTime, ...(updated as object) };
              setVisits(prev => prev.map(v => (v.id === correcting.id ? { ...v, ...patch, id: v.id } : v)));
              setAllLoadedVisits(prev => prev.map(v => (v.id === correcting.id ? { ...v, ...patch, id: v.id } : v)));
              setCorrecting(null);
            } catch (e) {
              setCorrectError(isApiError(e) && e.statusCode < 500 ? e.message : 'Could not correct the visit');
            } finally {
              setCorrectBusy(false);
            }
          }}
        />
      )}
      {canCancelVisit && <CancelVisitModal
        visitId={cancelModal.visitId}
        patientId={cancelModal.patientId}
        isOpen={cancelModal.open}
        onClose={() => setCancelModal({ open: false, visitId: '', patientId: '' })}
        onSuccess={(updatedVisit) => {
          // Update the cancelled visit in local state so the UI reflects immediately
          setVisits(prev => prev.map(v =>
            v.id === updatedVisit.id ? { ...v, ...updatedVisit } : v
          ));
          setAllLoadedVisits(prev => prev.map(v =>
            v.id === updatedVisit.id ? { ...v, ...updatedVisit } : v
          ));
          setCancelModal({ open: false, visitId: '', patientId: '' });
        }}
        onConfirm={async (payload: CancelVisitRequest) => {
          const { cancelVisit } = await import('../../services/patientmr/visits.service');
          return cancelVisit(cancelModal.patientId, cancelModal.visitId, payload);
        }}
      />}
    </div>
  );
};

export default VisitHistory;